import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Check } from 'lucide-react';

export default function OrganizationCreate() {
  const navigate = useNavigate();
  const presetType = new URLSearchParams(window.location.search).get('type'); // /admin/marketers → ?type=marketer
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', type: ['medical_practice', 'law_firm', 'marketer'].includes(presetType) ? presetType : 'medical_practice' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const res = await fetch(`${API_URL}/admin/invite-org`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          email: form.email,
          first_name: form.firstName,
          last_name: form.lastName,
          org_type: form.type,
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to send invite');

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';

  if (success) {
    return (
      <div className="flex-1 overflow-y-auto bg-primary">
        <div className="max-w-[480px] mx-auto px-4 md:px-7 py-12 text-center">
          <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4">
            <Check size={24} strokeWidth={2.5} />
          </div>
          <h1 className="text-[22px] font-bold text-primary-c mb-2">Invitation Sent</h1>
          <p className="text-[13px] text-muted-c mb-6">
            An invitation has been sent to <strong className="text-primary-c">{form.email}</strong>.
            They'll receive an email to set up their {form.type === 'law_firm' ? 'law firm' : form.type === 'marketer' ? 'marketer account (invite-only: same tools as a law firm, free)' : 'medical practice'}.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => { setSuccess(false); setForm({ email: '', firstName: '', lastName: '', type: 'medical_practice' }); }}
              className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
            >
              Invite Another
            </button>
            <button
              onClick={() => navigate('/admin/organizations')}
              className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
            >
              View Organizations
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-primary">
      <div className="max-w-[480px] mx-auto px-4 md:px-7 py-6">
        <h1 className="text-[22px] font-bold text-primary-c mb-2">Invite Organization</h1>
        <p className="text-[13px] text-muted-c mb-6">Send an invite to a new organization owner. They'll complete the setup themselves.</p>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Organization Type *</label>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { value: 'medical_practice', label: 'Medical Practice', color: '#10B981' },
                  { value: 'law_firm', label: 'Law Firm', color: '#F59E0B' },
                  { value: 'marketer', label: 'Marketer', color: '#8B5CF6' },
                ].map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => update('type', t.value)}
                    className={`p-3.5 rounded-[10px] text-[13px] font-semibold transition-all border text-left ${
                      form.type === t.value
                        ? 'text-white shadow-md border-transparent'
                        : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface'
                    }`}
                    style={form.type === t.value ? { background: t.color } : undefined}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3.5">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">First Name *</label>
                <input type="text" className={inputClass} value={form.firstName} onChange={e => update('firstName', e.target.value)} required placeholder="John" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">Last Name *</label>
                <input type="text" className={inputClass} value={form.lastName} onChange={e => update('lastName', e.target.value)} required placeholder="Doe" />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Email *</label>
              <input type="email" className={inputClass} value={form.email} onChange={e => update('email', e.target.value)} required placeholder="owner@practice.com" />
            </div>

            <div className="flex justify-end gap-2.5 pt-5 border-t border-theme">
              <button type="button" onClick={() => navigate(-1)} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="px-5 py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50">
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
